import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import HomeBanners from '../components/home/HomeBanners';
import SpecialsStrip from '../components/home/SpecialsStrip';
import TestFinder from '../components/home/TestFinder';
import HowItWorks from '../components/home/HowItWorks';
import TrustianTracker from '../components/home/TrustianTracker';
import StatsBand from '../components/home/StatsBand';
import Accreditations from '../components/home/Accreditations';
import MembershipTeaser from '../components/home/MembershipTeaser';
import Testimonials from '../components/home/Testimonials';
import CitiesWeServe from '../components/home/CitiesWeServe';
import AppDownload from '../components/home/AppDownload';
import CTABand from '../components/home/CTABand';
import SectionHeader from '../components/common/SectionHeader';
import TestCard from '../components/common/TestCard';
import PackageCard from '../components/common/PackageCard';
import BlogCard from '../components/common/BlogCard';
import Reveal from '../components/common/Reveal';
import { PackagesShimmer } from '../components/shimmer';
import { getPopularTests } from '../data/tests';
import { BLOGS } from '../data/content';
import { ROUTES } from '../constants/routes';
import { useGetPackagesQuery } from '../store/edosApi';
import {
  useListRecentTestsQuery,
  useListPopularTestsQuery,
  useListPopularPackagesQuery,
  useListBlogArticlesQuery,
  useListBlogCategoriesQuery,
  useGetMembershipSettingsQuery,
} from '../store/siteContentApi';
import { mapApiToPackage } from '../utils/mapApiPackage';

// "Mar 2026" from either published_at (preferred) or createdAt — the meta
// line on BlogCard shows a short month+year.
const formatBlogDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};

// Convert an API blog article + matching category into the shape
// BlogCard expects. The admin schema has no author field, so fall back
// to "TrustLab Editorial" rather than leaving that meta segment empty.
const mapApiArticleToCard = (article, category) => ({
  id: article._id || article.slug,
  slug: article.slug,
  icon: category?.icon || '🧪',
  bg: article.gradient
    || category?.gradient
    || 'linear-gradient(135deg, #C8EDD8, #8DCFAA)',
  // Category label on API is like "🩸 Diabetes" — strip the leading
  // emoji since BlogCard shows the icon separately inside the banner.
  category: (category?.label || '').replace(/^[^\s]+\s/, '') || 'Health',
  title: article.title,
  excerpt: article.excerpt,
  author: 'TrustLab Editorial',
  // Stored as "5 min read"; BlogCard appends " read" itself, so strip it.
  readTime: String(article.read_time || '5 min').replace(/\s*read\s*$/i, ''),
  date: formatBlogDate(article.published_at || article.createdAt),
});

const Home = () => {
  const navigate = useNavigate();

  // Popular tests — admin-curated first (managed at /admin/settings/home/popular-tests).
  // If the admin hasn't picked any yet, fall back to recent booking activity
  // (most-recent unique tests, deduped on the backend), and finally to the
  // static catalogue so the section never renders empty on fresh installs.
  const { data: curatedTests = [] } = useListPopularTestsQuery();
  const { data: recentTests  = [] } = useListRecentTestsQuery(8, { skip: curatedTests.length > 0 });
  const popularTests = curatedTests.length > 0
    ? curatedTests.slice(0, 8)
    : recentTests.length > 0
      ? recentTests
      : getPopularTests().slice(0, 8);

  // Packages — admin-curated first (managed at /admin/settings/home/popular-packages).
  // If the admin hasn't picked any, fall back to the first page of EDOS
  // packages so the section never renders empty.
  const { data: curatedPackages = [] } = useListPopularPackagesQuery();
  const { data: pkgData, isLoading: pkgLoading } = useGetPackagesQuery(
    { page: 1 },
    { skip: curatedPackages.length > 0 },
  );
  const topPackages = curatedPackages.length > 0
    ? curatedPackages.slice(0, 4)
    : (pkgData?.items ?? []).slice(0, 4).map(mapApiToPackage);

  // Same flag the navbar uses to gate the Membership menu item — when
  // the admin disables membership in app-settings, both the nav link
  // AND this homepage teaser hide together. Default to false so the
  // section doesn't flash before the API resolves.
  const { data: membershipSettings } = useGetMembershipSettingsQuery();
  const membershipEnabled = membershipSettings?.is_enabled ?? false;

  // Blog preview — same API the /blog page consumes. Take the first 3
  // active articles (backend returns them already ordered). Fall back to
  // the static BLOGS list when the admin hasn't published any yet so
  // fresh installs don't show an empty section.
  const { data: apiArticles = [] } = useListBlogArticlesQuery();
  const { data: apiCategories = [] } = useListBlogCategoriesQuery();
  const categoryBySlug = apiCategories.reduce((m, c) => {
    m[c.slug] = c;
    return m;
  }, {});
  const featuredBlogs = apiArticles.length > 0
    ? apiArticles.slice(0, 3).map((a) => mapApiArticleToCard(a, categoryBySlug[a.category_slug]))
    : BLOGS.slice(0, 3);

  return (
    <Box>
      {/* 1. Banner carousel — placeholder slot for admin-curated promo
          banners. Auto-rotates every 5s; pauses on hover. */}
      <HomeBanners />

      {/* 2. Specials / Offers strip */}
      <Reveal direction="up"><SpecialsStrip /></Reveal>

      {/* 3. Test Finder (dark green) */}
      <Reveal direction="up"><TestFinder /></Reveal>

      {/* 4. Popular Tests */}
      <Box sx={{ bgcolor: 'var(--cream)', py: { xs: 8, md: 13 } }}>
        <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 } }}>
          <Reveal direction="up">
            <SectionHeader
              eyebrow="Popular Tests"
              title="Most booked this week"
              action={
                <Button
                  variant="outlined"
                  onClick={() => navigate(ROUTES.TESTS)}
                  sx={{ borderRadius: 100, px: 2.5, py: 1, fontSize: 13, fontWeight: 600, textTransform: 'none' }}
                >
                  View all 1700+ tests →
                </Button>
              }
            />
          </Reveal>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: { xs: 2, md: 2.5 },
              width: '100%',
            }}
          >
            {popularTests.map((t, i) => (
              <Reveal key={t.id} direction="up" delay={(i % 4) * 80} sx={{ height: '100%' }}>
                <TestCard test={t} />
              </Reveal>
            ))}
          </Box>
        </Container>
      </Box>

      {/* 5. Health Packages */}
      <Box sx={{ bgcolor: 'var(--parchment)', py: { xs: 8, md: 13 } }}>
        <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 } }}>
          <Reveal direction="up">
            <SectionHeader
              eyebrow="Health Checkups"
              title="Our most popular packages"
              description="Pathologist-designed for specific life stages. Each package targets the biomarkers that matter most for your age, gender, condition, lifestyle and goals. Not sure which one fits you? Try Smart Recommendation →"
              action={
                <Button
                  variant="outlined"
                  onClick={() => navigate(ROUTES.PACKAGES)}
                  sx={{ borderRadius: 100, px: 2.5, py: 1, fontSize: 13, fontWeight: 600, textTransform: 'none' }}
                >
                  View all packages →
                </Button>
              }
            />
          </Reveal>
          {pkgLoading ? (
            <PackagesShimmer count={4} />
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(4, 1fr)',
                },
                gap: { xs: 2, md: 2.5 },
                width: '100%',
              }}
            >
              {topPackages.map((p, i) => (
                <Reveal key={p.id} direction="up" delay={(i % 4) * 80} sx={{ height: '100%' }}>
                  <PackageCard pkg={p} hideDiscount hideBadge />
                </Reveal>
              ))}
            </Box>
          )}
        </Container>
      </Box>

      {/* 6. How It Works */}
      <Reveal direction="up"><HowItWorks /></Reveal>

      {/* 7. Trustian Tracker */}
      <Reveal direction="up"><TrustianTracker /></Reveal>

      {/* 8. Stats Band */}
      <Reveal direction="up"><StatsBand /></Reveal>

      {/* 9. Why TrustLab */}
      {/* <Reveal direction="up"><WhyTrustLab /></Reveal> */}

      {/* 10. Accreditations */}
      <Reveal direction="up"><Accreditations /></Reveal>

      {/* 11. Membership Teaser — gated on the same is_enabled flag as
          the navbar's Membership link. When membership is off, the
          section is removed entirely (no empty space). */}
      {membershipEnabled && (
        <Reveal direction="up"><MembershipTeaser /></Reveal>
      )}

      {/* 12. Testimonials marquee */}
      <Reveal direction="up"><Testimonials /></Reveal>

      {/* 13. Cities We Serve */}
      <Reveal direction="up"><CitiesWeServe /></Reveal>

      {/* 14. Blog Preview */}
      <Box sx={{ bgcolor: '#fff', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 } }}>
          <Reveal direction="up">
            <SectionHeader
              eyebrow="Health Reads"
              title="Know your numbers"
              description="Doctor-authored guides to make your lab reports understandable and actionable."
              action={
                <Button
                  variant="outlined"
                  onClick={() => navigate(ROUTES.BLOG)}
                  sx={{ borderRadius: 100, px: 2.5, py: 1, fontSize: 13, fontWeight: 600, textTransform: 'none' }}
                >
                  All articles →
                </Button>
              }
            />
          </Reveal>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: { xs: 2, md: 2.5 },
              width: '100%',
            }}
          >
            {featuredBlogs.map((b, i) => (
              <Reveal key={b.id} direction="up" delay={(i % 3) * 100} sx={{ height: '100%' }}>
                <BlogCard
                  blog={b}
                  onClick={() => navigate(b.slug ? `${ROUTES.BLOG}/${b.slug}` : ROUTES.BLOG)}
                />
              </Reveal>
            ))}
          </Box>
        </Container>
      </Box>

      {/* 15. App Download */}
      <Reveal direction="up"><AppDownload /></Reveal>

      {/* 16. CTA Band */}
      <Reveal direction="up"><CTABand /></Reveal>
    </Box>
  );
};

export default Home;
